import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import InvoiceModal from '../components/InvoiceModal';
import OfflinePaymentModal from '../components/OfflinePaymentModal';

interface Invoice {
  id: string;
  amount: number | string;
  description: string;
  status: 'PAID' | 'PENDING' | 'OVERDUE';
  userId: string;
  dueDate: string;
  paymentDate?: string;
  createdAt: string;
  updatedAt: string;
  user?: { name: string; email: string; contactNumber?: string };
  propertyGroup?: {
    name: string;
    description?: string;
    addresses?: { address: string; city: string; state: string; zipCode: string }[];
  };
}

interface PaidInvoiceDetails {
  id: string;
  description: string;
  amount: number;
  paidAt: string;
  propertyGroupName?: string;
}

function isAxiosError(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const buildMonthlyChartData = (invoices: Invoice[], year: number) => {
  const data = MONTH_LABELS.map((label, i) => ({ month: label, invoiced: 0, collected: 0, index: i }));
  invoices.forEach(inv => {
    const d = new Date(inv.createdAt);
    if (d.getFullYear() === year) {
      const m = d.getMonth();
      data[m].invoiced += Number(inv.amount);
      if (inv.status === 'PAID') data[m].collected += Number(inv.amount);
    }
  });
  return data.map(({ index, ...rest }) => rest);
};

const buildWeeklyChartData = (invoices: Invoice[], month: number, year: number) => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const weeks = [
    { label: 'Wk 1 (1–7)', start: 1, end: 7 },
    { label: 'Wk 2 (8–14)', start: 8, end: 14 },
    { label: 'Wk 3 (15–21)', start: 15, end: 21 },
    { label: `Wk 4 (22–${Math.min(28, daysInMonth)})`, start: 22, end: 28 },
  ];
  if (daysInMonth > 28) {
    weeks.push({ label: `Wk 5 (29–${daysInMonth})`, start: 29, end: daysInMonth });
  }
  const chartData = weeks.map(w => ({ week: w.label, invoiced: 0, collected: 0 }));
  invoices.forEach(inv => {
    const d = new Date(inv.createdAt);
    const day = d.getDate();
    const idx = weeks.findIndex(w => day >= w.start && day <= w.end);
    if (idx !== -1) {
      chartData[idx].invoiced += Number(inv.amount);
      if (inv.status === 'PAID') chartData[idx].collected += Number(inv.amount);
    }
  });
  return chartData;
};

const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({
      month: d.getMonth() + 1,
      year: d.getFullYear(),
      label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    });
  }
  return options;
};

const formatCurrency = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;

const formatDate = (s: string) =>
  new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getStatusStyle = (status: string) => {
  if (status === 'PAID') return 'text-emerald-400 bg-emerald-500/15 border border-emerald-500/20';
  if (status === 'OVERDUE') return 'text-red-400 bg-red-500/15 border border-red-500/20';
  return 'text-amber-400 bg-amber-500/15 border border-amber-500/20';
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#16161a] border border-white/10 rounded-xl p-3 shadow-xl">
      <p className="text-white font-medium text-sm mb-2">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: <span className="font-semibold">${Number(entry.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </p>
      ))}
    </div>
  );
};

const Payments: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { groupSlug } = useParams<{ groupSlug?: string }>();
  const cashFlowPath = groupSlug ? `/${groupSlug}/cash-flow` : '/cash-flow';
  const expensesPath = groupSlug ? `/${groupSlug}/expenses` : '/expenses';

  const [allYearInvoices, setAllYearInvoices] = useState<Invoice[]>([]);
  const [selectedMonthInvoices, setSelectedMonthInvoices] = useState<Invoice[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [chartMode, setChartMode] = useState<'year' | 'monthly'>('year');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingYear, setLoadingYear] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showOfflinePaymentModal, setShowOfflinePaymentModal] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paidInvoiceDetails, setPaidInvoiceDetails] = useState<PaidInvoiceDetails | null>(null);
  const [invoiceSearchOpen, setInvoiceSearchOpen] = useState(false);
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState('');
  const [payingInvoiceId, setPayingInvoiceId] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState<{
    connected: boolean;
    status: string | null;
    chargesEnabled?: boolean;
    detailsSubmitted?: boolean;
    payoutsEnabled?: boolean;
    email?: string;
    businessName?: string;
  } | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectMessage, setConnectMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const monthOptions = getMonthOptions();
  // Fetch all invoices for the selected year (for stats + monthly chart)
  const fetchYearInvoices = useCallback(async () => {
    if (!user) return;
    const grpId = localStorage.getItem('selectedPropertyGroup');
    const hdrs: Record<string, string> = grpId ? { 'x-selected-group': grpId } : {};
    setLoadingYear(true);
    try {
      const res = await api.get<Invoice[]>('/api/invoices', {
        headers: hdrs,
        params: { year: selectedYear }
      });
      setAllYearInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching year invoices:', err);
      setAllYearInvoices([]);
    } finally {
      setLoadingYear(false);
    }
  }, [user, selectedYear]);

  // Fetch invoices for the selected month (for weekly chart detail)
  const fetchMonthInvoices = useCallback(async () => {
    if (!user) return;
    const grpId = localStorage.getItem('selectedPropertyGroup');
    const hdrs: Record<string, string> = grpId ? { 'x-selected-group': grpId } : {};
    try {
      const params: any = { month: selectedMonth, year: selectedYear };
      if (user.role === 'RESIDENT') params.userId = user.id;
      const res = await api.get<Invoice[]>('/api/invoices', { headers: hdrs, params });
      setSelectedMonthInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching month invoices:', err);
      setSelectedMonthInvoices([]);
    }
  }, [user, selectedMonth, selectedYear]);

  useEffect(() => { fetchYearInvoices(); }, [fetchYearInvoices]);
  useEffect(() => { fetchMonthInvoices(); }, [fetchMonthInvoices]);

  // Fetch Stripe Connect status (admin only)
  const fetchConnectStatus = useCallback(async () => {
    if (user?.role !== 'ADMIN') return;
    try {
      const res = await api.get<typeof connectStatus>('/api/stripe/connect/status');
      setConnectStatus(res.data);
    } catch (err) {
      console.error('Error fetching Connect status:', err);
    }
  }, [user?.role]);

  useEffect(() => { fetchConnectStatus(); }, [fetchConnectStatus]);

  // Handle Stripe Connect redirect back
  useEffect(() => {
    const connectParam = searchParams.get('stripe_connect');
    if (connectParam === 'success') {
      setConnectMessage({ text: 'Stripe account connected! Your account is being verified.', type: 'success' });
      fetchConnectStatus();
      navigate('/payments', { replace: true });
    } else if (connectParam === 'refresh') {
      setConnectMessage({ text: 'Onboarding was interrupted. Please complete setup to start accepting payments.', type: 'error' });
      navigate('/payments', { replace: true });
    }
  }, [searchParams, navigate, fetchConnectStatus]);

  const handleConnectStripe = async () => {
    setConnectLoading(true);
    try {
      const res = await api.post<{ url: string }>('/api/stripe/connect/create-account');
      window.location.href = res.data.url;
    } catch (err: any) {
      setConnectMessage({ text: err.response?.data?.error || 'Failed to start Stripe setup', type: 'error' });
      setConnectLoading(false);
    }
  };

  const handleResumOnboarding = async () => {
    setConnectLoading(true);
    try {
      const res = await api.post<{ url: string }>('/api/stripe/connect/resume-onboarding');
      window.location.href = res.data.url;
    } catch (err: any) {
      setConnectMessage({ text: err.response?.data?.error || 'Failed to resume setup', type: 'error' });
      setConnectLoading(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    setConnectLoading(true);
    try {
      const res = await api.post<{ url: string }>('/api/stripe/connect/dashboard-link');
      window.open(res.data.url, '_blank');
    } catch (err: any) {
      setConnectMessage({ text: err.response?.data?.error || 'Failed to open dashboard', type: 'error' });
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!window.confirm('Disconnect your Stripe account? Residents will no longer be able to pay online until you reconnect.')) return;
    try {
      await api.post('/api/stripe/connect/disconnect');
      setConnectStatus({ connected: false, status: null });
      setConnectMessage({ text: 'Stripe account disconnected.', type: 'success' });
    } catch (err: any) {
      setConnectMessage({ text: 'Failed to disconnect', type: 'error' });
    }
  };

  // Handle payment success redirect from Stripe
  useEffect(() => {
    const success = searchParams.get('success');
    const invoiceId = searchParams.get('invoice');
    if (success === 'true' && invoiceId) {
      const fetchPaidInvoice = async () => {
        try {
          const res = await api.get<any>(`/api/invoices/${invoiceId}`);
          const inv = res.data;
          setPaidInvoiceDetails({
            id: inv.id,
            description: inv.description,
            amount: Number(inv.amount),
            paidAt: inv.paymentDate || new Date().toISOString(),
            propertyGroupName: inv.propertyGroup?.name
          });
        } catch {
          setPaidInvoiceDetails({ id: invoiceId, description: 'Payment', amount: 0, paidAt: new Date().toISOString() });
        }
        setShowPaymentSuccess(true);
        navigate('/payments', { replace: true });
      };
      fetchPaidInvoice();
    }
  }, [searchParams, navigate]);

  const handleInvoiceSubmit = async (invoiceData: any) => {
    setIsLoading(true);
    setError(null);
    setShowInvoiceModal(false);
    try {
      const res = await api.post<Invoice[]>('/api/invoices', invoiceData);
      const created = res.data[0];
      const d = new Date(created.createdAt);
      setSelectedMonth(d.getMonth() + 1);
      setSelectedYear(d.getFullYear());
      await fetchYearInvoices();
      await fetchMonthInvoices();
      setError(null);
    } catch (err: unknown) {
      let msg = 'Failed to create invoices';
      if (isAxiosError(err)) {
        if (err.response?.status === 403) msg = 'Only administrators can create invoices';
        else if (err.response?.data?.message) msg = err.response.data.message;
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOfflinePaymentSuccess = async () => {
    await fetchYearInvoices();
    await fetchMonthInvoices();
    setError(null);
  };

  const handleDeleteInvoice = async (invoiceId: string, description: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete invoice "${description}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/invoices/${invoiceId}`);
      setAllYearInvoices(prev => prev.filter(i => i.id !== invoiceId));
      setSelectedMonthInvoices(prev => prev.filter(i => i.id !== invoiceId));
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.message) alert(err.response.data.message);
      else alert('Failed to delete invoice');
    }
  };

  // PDF FEATURE START
  const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);

  const handleDownloadPdf = async (inv: Invoice) => {
    setDownloadingPdfId(inv.id);
    try {
      const res = await api.post('/api/pdf/generate', { invoiceId: inv.id }, { responseType: 'blob' });
      const blob = new Blob([res.data as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${inv.id.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdfId(null);
    }
  };
  // PDF FEATURE END

  const handlePayInvoice = async (invoiceId: string) => {
    setPayingInvoiceId(invoiceId);
    setPayError(null);
    try {
      const res = await api.post<{ url: string }>('/api/stripe/create-invoice-payment', { invoiceId });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Payment failed. Please try again.';
      setPayError(msg);
    } finally {
      setPayingInvoiceId(null);
    }
  };

  // ── Resident View ──────────────────────────────────────────────────────────
  if (user?.role === 'RESIDENT') {
    // Backend already filters by userId for residents, so allYearInvoices only contains this user's invoices
    const myInvoices = allYearInvoices;
    const pending = myInvoices.filter(p => p.status !== 'PAID');
    const paid = myInvoices.filter(p => p.status === 'PAID');
    const outstanding = pending.reduce((s, p) => s + Number(p.amount), 0);
    const totalPaid = paid.reduce((s, p) => s + Number(p.amount), 0);
    const nextDue = [...pending].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0];

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="floating-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">Outstanding Balance</p>
                <p className="text-3xl font-bold text-red-400 mt-1">${outstanding.toFixed(2)}</p>
                <p className="text-xs text-white/40 mt-1">{pending.length} pending invoice{pending.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>
          <div className="floating-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">Paid This Year</p>
                <p className="text-3xl font-bold text-emerald-400 mt-1">${totalPaid.toFixed(2)}</p>
                <p className="text-xs text-white/40 mt-1">{paid.length} payment{paid.length !== 1 ? 's' : ''} made</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="floating-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">Next Payment Due</p>
                <p className="text-3xl font-bold text-[#22d3ee] mt-1">
                  {nextDue ? `$${Number(nextDue.amount).toFixed(2)}` : '$0.00'}
                </p>
                <p className="text-xs text-white/40 mt-1">
                  {nextDue ? `Due ${formatDate(nextDue.dueDate)}` : 'No pending payments'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#22d3ee]/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-[#22d3ee]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white">My Invoices</h2>
            {pending.length > 0 && nextDue && (
              <button
                onClick={() => handlePayInvoice(nextDue.id)}
                disabled={payingInvoiceId === nextDue.id}
                className="btn-primary flex items-center gap-2 disabled:opacity-60"
              >
                {payingInvoiceId === nextDue.id ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing…
                  </>
                ) : (
                  'Pay Next Due'
                )}
              </button>
            )}
          </div>

          {payError && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3">
              <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-400 text-sm">{payError}</p>
              <button onClick={() => setPayError(null)} className="ml-auto text-white/30 hover:text-white/60">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {loadingYear ? (
            <div className="flex items-center justify-center py-10 gap-3 text-white/40">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-sm">Loading invoices…</span>
            </div>
          ) : myInvoices.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-white/40">No invoices yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myInvoices.map(inv => (
                <div key={inv.id} className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/8 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{inv.description}</p>
                    <p className="text-sm text-white/50 mt-0.5">Due {formatDate(inv.dueDate)}</p>
                  </div>
                  <div className="flex items-center gap-4 ml-4">
                    <span className="text-lg font-semibold text-white">${Number(inv.amount).toFixed(2)}</span>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusStyle(inv.status)}`}>
                      {inv.status}
                    </span>
                    {inv.status !== 'PAID' && (
                      <button
                        onClick={() => handlePayInvoice(inv.id)}
                        disabled={payingInvoiceId === inv.id}
                        className="btn-secondary text-sm px-4 py-2 flex items-center gap-1.5 disabled:opacity-60"
                      >
                        {payingInvoiceId === inv.id ? (
                          <>
                            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Processing…
                          </>
                        ) : (
                          'Pay Now'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="floating-card">
          <h2 className="text-xl font-bold text-white mb-2">Need Help?</h2>
          <p className="text-white/50 mb-4">Questions about your invoices? Contact your property manager.</p>
          <button onClick={() => navigate('/issues')} className="btn-secondary flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Submit a Request
          </button>
        </div>
      </div>
    );
  }

  // ── Admin View ─────────────────────────────────────────────────────────────
  const totalBilled = allYearInvoices.reduce((s, i) => s + Number(i.amount), 0);
  const totalCollected = allYearInvoices.filter(i => i.status === 'PAID').reduce((s, i) => s + Number(i.amount), 0);
  const totalOutstanding = totalBilled - totalCollected;
  const collectionRate = totalBilled > 0 ? (totalCollected / totalBilled) * 100 : 0;

  const monthlyChartData = buildMonthlyChartData(allYearInvoices, selectedYear);
  const weeklyChartData = buildWeeklyChartData(selectedMonthInvoices, selectedMonth, selectedYear);
  const chartData = chartMode === 'year' ? monthlyChartData : weeklyChartData;
  const xKey = chartMode === 'year' ? 'month' : 'week';

  const recentInvoices = [...allYearInvoices].sort((a, b) => {
    const statusOrder: Record<string, number> = { PENDING: 0, OVERDUE: 1, PAID: 2 };
    const sDiff = (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
    if (sDiff !== 0) return sDiff;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  const pendingCount = allYearInvoices.filter(i => i.status === 'PENDING').length;

  const activeInvoiceList = chartMode === 'monthly' ? selectedMonthInvoices : recentInvoices;
  const filteredInvoices = invoiceSearchQuery.trim()
    ? activeInvoiceList.filter(inv => {
        const q = invoiceSearchQuery.toLowerCase();
        return (
          inv.user?.name?.toLowerCase().includes(q) ||
          inv.user?.email?.toLowerCase().includes(q) ||
          inv.description?.toLowerCase().includes(q) ||
          inv.status?.toLowerCase().includes(q)
        );
      })
    : activeInvoiceList;
  const overdueCount = allYearInvoices.filter(i => i.status === 'OVERDUE').length;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl">
          {error}
          <button onClick={() => setError(null)} className="ml-3 text-red-300 hover:text-red-100">✕</button>
        </div>
      )}

      {/* ── Stripe Connect Gateway ── */}
      <div className="floating-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#635bff]/15 flex items-center justify-center">
              <svg className="w-5 h-5 text-[#635bff]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-white font-semibold">Payment Gateway</h2>
              <p className="text-white/50 text-xs">Connect your Stripe account to accept online resident payments</p>
            </div>
          </div>
          {connectStatus?.connected && connectStatus.status === 'active' && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Active
            </span>
          )}
          {connectStatus?.connected && connectStatus.status === 'pending_onboarding' && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-amber-400 bg-amber-500/10 border border-amber-500/20 px-3 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              Setup Incomplete
            </span>
          )}
          {connectStatus !== null && !connectStatus.connected && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-white/40 bg-white/5 border border-white/10 px-3 py-1 rounded-full">
              Not Connected
            </span>
          )}
        </div>

        {connectMessage && (
          <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm flex items-center justify-between ${
            connectMessage.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
          }`}>
            <span>{connectMessage.text}</span>
            <button onClick={() => setConnectMessage(null)} className="ml-2 opacity-60 hover:opacity-100">✕</button>
          </div>
        )}

        {/* Not connected */}
        {connectStatus !== null && !connectStatus.connected && (
          <div className="space-y-4">
            {/* Setup guide */}
            <div className="p-4 rounded-xl bg-[#635bff]/5 border border-[#635bff]/15">
              <p className="text-white/80 text-sm font-medium mb-3">Before connecting, you need to enable Stripe Connect on your Stripe account:</p>
              <ol className="space-y-2 mb-4">
                <li className="flex items-start gap-2 text-sm text-white/60">
                  <span className="w-5 h-5 rounded-full bg-[#635bff]/20 text-[#635bff] flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                  <span>Go to <a href="https://dashboard.stripe.com/connect" target="_blank" rel="noopener noreferrer" className="text-[#635bff] hover:text-[#7b73ff] underline underline-offset-2">dashboard.stripe.com/connect</a> and click <strong className="text-white/80">"Get started with Connect"</strong></span>
                </li>
                <li className="flex items-start gap-2 text-sm text-white/60">
                  <span className="w-5 h-5 rounded-full bg-[#635bff]/20 text-[#635bff] flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                  <span>Choose <strong className="text-white/80">"Platform or marketplace"</strong> as your use case and complete the brief setup form</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-white/60">
                  <span className="w-5 h-5 rounded-full bg-[#635bff]/20 text-[#635bff] flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                  <span>Once approved, come back here and click <strong className="text-white/80">"Connect Stripe"</strong> to link your account</span>
                </li>
              </ol>
              <div className="flex items-center gap-3">
                <a
                  href="https://dashboard.stripe.com/connect"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-[#635bff] hover:bg-[#5a52e8] text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Open Stripe Connect →
                </a>
                <button
                  onClick={handleConnectStripe}
                  disabled={connectLoading}
                  className="px-4 py-2 bg-white/8 hover:bg-white/12 disabled:opacity-60 text-white/80 rounded-lg text-sm font-medium transition-colors border border-white/10"
                >
                  {connectLoading ? 'Setting up…' : 'Already enabled — Connect now'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pending onboarding */}
        {connectStatus?.connected && connectStatus.status === 'pending_onboarding' && (
          <div className="flex items-center justify-between p-4 rounded-xl bg-amber-500/5 border border-amber-500/15">
            <div>
              <p className="text-amber-300 text-sm font-medium">Stripe setup incomplete</p>
              <p className="text-white/40 text-xs mt-0.5">Finish the onboarding process to enable payments.</p>
            </div>
            <button
              onClick={handleResumOnboarding}
              disabled={connectLoading}
              className="ml-4 flex-shrink-0 px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {connectLoading ? 'Loading…' : 'Complete Setup'}
            </button>
          </div>
        )}

        {/* Active */}
        {connectStatus?.connected && connectStatus.status === 'active' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/15">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-white text-sm font-medium">
                    {connectStatus.businessName || connectStatus.email || 'Stripe account'}
                  </p>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {connectStatus.chargesEnabled && <span className="text-xs text-emerald-400">✓ Charges enabled</span>}
                    {connectStatus.payoutsEnabled && <span className="text-xs text-emerald-400">✓ Payouts enabled</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <button
                  onClick={handleOpenStripeDashboard}
                  disabled={connectLoading}
                  className="px-4 py-2 bg-[#635bff] hover:bg-[#5a52e8] disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {connectLoading ? 'Loading…' : 'Open Dashboard'}
                </button>
                <button
                  onClick={handleDisconnectStripe}
                  className="px-3 py-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg text-sm transition-colors"
                >
                  Disconnect
                </button>
              </div>
            </div>
            {/* Routing confirmation */}
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#635bff]/5 border border-[#635bff]/15">
              <svg className="w-4 h-4 text-[#635bff] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <p className="text-xs text-white/60">
                <span className="text-[#635bff] font-medium">Payments routing active</span> — resident invoice payments go directly to your connected Stripe account and are paid out to your bank on your normal Stripe payout schedule.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="floating-card">
          <p className="text-white/50 text-xs font-medium uppercase tracking-wide">Total Billed</p>
          <p className="text-2xl font-bold text-white mt-1">
            {loadingYear ? <span className="text-white/30 text-lg">—</span> : `$${totalBilled.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </p>
          <p className="text-xs text-white/40 mt-1">{allYearInvoices.length} invoices · {selectedYear}</p>
        </div>

        <div className="floating-card">
          <p className="text-white/50 text-xs font-medium uppercase tracking-wide">Collected</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">
            {loadingYear ? <span className="text-white/30 text-lg">—</span> : `$${totalCollected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </p>
          <p className="text-xs text-white/40 mt-1">{allYearInvoices.filter(i => i.status === 'PAID').length} paid invoices</p>
        </div>

        <div className="floating-card">
          <p className="text-white/50 text-xs font-medium uppercase tracking-wide">Outstanding</p>
          <p className={`text-2xl font-bold mt-1 ${totalOutstanding > 0 ? 'text-amber-400' : 'text-white'}`}>
            {loadingYear ? <span className="text-white/30 text-lg">—</span> : `$${totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </p>
          <p className="text-xs text-white/40 mt-1">
            {pendingCount} pending{overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}
          </p>
        </div>

        <div className="floating-card relative overflow-hidden">
          <p className="text-white/50 text-xs font-medium uppercase tracking-wide">Collection Rate</p>
          <p className={`text-2xl font-bold mt-1 ${collectionRate >= 80 ? 'text-emerald-400' : collectionRate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
            {loadingYear ? <span className="text-white/30 text-lg">—</span> : `${collectionRate.toFixed(1)}%`}
          </p>
          <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${collectionRate >= 80 ? 'bg-emerald-400' : collectionRate >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
              style={{ width: `${Math.min(collectionRate, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* ── Chart + Actions row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="floating-card lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h2 className="text-xl font-bold text-white">Revenue Overview</h2>
              <p className="text-white/40 text-sm mt-0.5">
                {chartMode === 'year'
                  ? `Monthly breakdown · ${selectedYear}`
                  : `Weekly breakdown · ${new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Year selector */}
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(parseInt(e.target.value))}
                className="input-field text-sm py-2"
              >
                {[0, 1, 2].map(i => {
                  const y = new Date().getFullYear() - i;
                  return <option key={y} value={y} className="bg-[#1a1a1d]">{y}</option>;
                })}
              </select>

              {/* Chart mode toggle */}
              <div className="flex rounded-lg overflow-hidden border border-white/10">
                <button
                  onClick={() => setChartMode('year')}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${chartMode === 'year' ? 'bg-[#22d3ee] text-white' : 'text-white/50 hover:text-white/80'}`}
                >
                  Year
                </button>
                <button
                  onClick={() => setChartMode('monthly')}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${chartMode === 'monthly' ? 'bg-[#22d3ee] text-white' : 'text-white/50 hover:text-white/80'}`}
                >
                  Month
                </button>
              </div>

              {/* Month selector (only in monthly mode) */}
              {chartMode === 'monthly' && (
                <select
                  value={`${selectedMonth}-${selectedYear}`}
                  onChange={e => {
                    const [m, y] = e.target.value.split('-').map(Number);
                    setSelectedMonth(m);
                    setSelectedYear(y);
                  }}
                  className="input-field text-sm py-2"
                >
                  {monthOptions.map(o => (
                    <option key={`${o.month}-${o.year}`} value={`${o.month}-${o.year}`} className="bg-[#1a1a1d]">
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {loadingYear ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-white/30 text-sm">Loading data…</div>
            </div>
          ) : allYearInvoices.length === 0 && chartMode === 'year' ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-16 h-16 rounded-full bg-[#22d3ee]/10 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-[#22d3ee]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-white/40 text-sm">No invoices for {selectedYear}</p>
              <p className="text-white/25 text-xs mt-1">Create your first invoice to see revenue data here</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} barGap={4}>
                <XAxis
                  dataKey={xKey}
                  tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => formatCurrency(v)}
                  width={55}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Legend
                  formatter={(value) => <span className="text-xs text-white/60">{value}</span>}
                  wrapperStyle={{ paddingTop: 16 }}
                />
                <Bar dataKey="invoiced" name="Invoiced" fill="rgba(34,211,238,0.5)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="collected" name="Collected" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Actions Panel */}
        <div className="floating-card flex flex-col gap-4">
          <div>
            <h3 className="text-lg font-bold text-white mb-1">Quick Actions</h3>
            <p className="text-white/40 text-sm">Manage your property payments</p>
          </div>

          <button
            onClick={() => setShowInvoiceModal(true)}
            disabled={isLoading}
            className="w-full btn-primary flex items-center justify-center gap-2 py-3"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {isLoading ? 'Creating…' : 'Create Invoice'}
          </button>

          <button
            onClick={() => setShowOfflinePaymentModal(true)}
            className="w-full btn-secondary flex items-center justify-center gap-2 py-3"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Record Offline Payment
          </button>

          {user?.role === 'ADMIN' && (
            <button
              onClick={() => navigate(cashFlowPath)}
              className="w-full flex items-center justify-between gap-3 py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500/15 to-teal-500/15 border border-emerald-500/30 hover:from-emerald-500/25 hover:to-teal-500/25 hover:border-emerald-400/50 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-white">View Cash Flow</p>
                  <p className="text-xs text-white/50">Income vs. obligations</p>
                </div>
              </div>
              <svg className="w-4 h-4 text-emerald-400/60 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {user?.role === 'ADMIN' && (
            <button
              onClick={() => navigate(expensesPath)}
              className="w-full flex items-center justify-between gap-3 py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500/15 to-orange-500/15 border border-amber-500/30 hover:from-amber-500/25 hover:to-orange-500/25 hover:border-amber-400/50 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-white">Track Expenses</p>
                  <p className="text-xs text-white/50">Repairs, supplies, services</p>
                </div>
              </div>
              <svg className="w-4 h-4 text-amber-400/60 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Status summary */}
          <div className="mt-2 space-y-2 pt-4 border-t border-white/5">
            <p className="text-white/40 text-xs uppercase tracking-wide font-medium">Status Breakdown</p>
            {[
              { label: 'Paid', count: allYearInvoices.filter(i => i.status === 'PAID').length, color: 'bg-emerald-400' },
              { label: 'Pending', count: pendingCount, color: 'bg-amber-400' },
              { label: 'Overdue', count: overdueCount, color: 'bg-red-400' },
            ].map(s => (
              <div key={s.label} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${s.color}`} />
                  <span className="text-sm text-white/60">{s.label}</span>
                </div>
                <span className="text-sm font-semibold text-white">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Invoice List ── */}
      <div className="floating-card">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold text-white">
              {chartMode === 'monthly'
                ? `Invoices · ${new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
                : `Invoices · ${selectedYear}`}
            </h2>
            <p className="text-white/40 text-sm mt-0.5">
              {invoiceSearchQuery.trim()
                ? `${filteredInvoices.length} result${filteredInvoices.length !== 1 ? 's' : ''}`
                : `${activeInvoiceList.length} invoice${activeInvoiceList.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {user?.role === 'ADMIN' && (
            <div className="flex items-center gap-2">
              {invoiceSearchOpen && (
                <input
                  autoFocus
                  type="text"
                  value={invoiceSearchQuery}
                  onChange={e => setInvoiceSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') { setInvoiceSearchOpen(false); setInvoiceSearchQuery(''); } }}
                  placeholder="Search invoices…"
                  className="w-44 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white placeholder-white/25 outline-none focus:border-white/20 transition-colors"
                />
              )}
              <button
                onClick={() => { setInvoiceSearchOpen(o => !o); if (invoiceSearchOpen) setInvoiceSearchQuery(''); }}
                className={`p-1.5 rounded-lg transition-colors ${invoiceSearchOpen ? 'text-white/60 bg-white/10' : 'text-white/25 hover:text-white/50'}`}
                title="Search invoices"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {filteredInvoices.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-7 h-7 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {invoiceSearchQuery.trim()
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                }
              </svg>
            </div>
            <p className="text-white/40 text-sm">
              {invoiceSearchQuery.trim() ? `No results for "${invoiceSearchQuery}"` : 'No invoices found'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-xs font-medium text-white/40 uppercase tracking-wide pb-3">Resident</th>
                  <th className="text-left text-xs font-medium text-white/40 uppercase tracking-wide pb-3">Description</th>
                  <th className="text-left text-xs font-medium text-white/40 uppercase tracking-wide pb-3 hidden sm:table-cell">Due Date</th>
                  <th className="text-right text-xs font-medium text-white/40 uppercase tracking-wide pb-3">Amount</th>
                  <th className="text-center text-xs font-medium text-white/40 uppercase tracking-wide pb-3">Status</th>
                  <th className="w-20 pb-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredInvoices.map(inv => (
                  <tr key={inv.id} className="group hover:bg-white/3 transition-colors">
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-white">{inv.user?.name || '—'}</p>
                      <p className="text-xs text-white/40">{inv.user?.email || ''}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm text-white/80 max-w-[180px] truncate">{inv.description}</p>
                    </td>
                    <td className="py-3 pr-4 hidden sm:table-cell">
                      <p className="text-sm text-white/60">{formatDate(inv.dueDate)}</p>
                    </td>
                    <td className="py-3 pr-4 text-right">
                      <p className="text-sm font-semibold text-white">${Number(inv.amount).toFixed(2)}</p>
                    </td>
                    <td className="py-3 text-center">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusStyle(inv.status)}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-3 pl-2">
                      <div className="flex items-center gap-1">
                        {/* PDF FEATURE START — Download PDF button */}
                        <button
                          onClick={() => handleDownloadPdf(inv)}
                          disabled={downloadingPdfId === inv.id}
                          className="opacity-0 group-hover:opacity-100 p-1.5 text-indigo-400 hover:bg-indigo-500/15 rounded-lg transition-all disabled:opacity-50"
                          title="Download PDF"
                        >
                          {downloadingPdfId === inv.id ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" />
                            </svg>
                          )}
                        </button>
                        {/* PDF FEATURE END */}
                        {user?.role === 'ADMIN' && (
                          <button
                            onClick={() => handleDeleteInvoice(inv.id, inv.description)}
                            className="opacity-0 group-hover:opacity-100 p-1.5 text-red-400 hover:bg-red-500/15 rounded-lg transition-all"
                            title="Delete invoice"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        onSubmit={handleInvoiceSubmit}
      />
      <OfflinePaymentModal
        isOpen={showOfflinePaymentModal}
        onClose={() => setShowOfflinePaymentModal(false)}
        onSuccess={handleOfflinePaymentSuccess}
      />

      {showPaymentSuccess && paidInvoiceDetails && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[#1a1a1d] border border-white/10 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-500/20">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
              <p className="text-white/50 mb-6">Your payment has been processed successfully.</p>

              <div className="bg-[#16161a] rounded-xl p-4 mb-6 text-left space-y-3">
                {[
                  { label: 'Description', value: paidInvoiceDetails.description },
                  { label: 'Amount Paid', value: `$${paidInvoiceDetails.amount.toFixed(2)}`, highlight: true },
                  { label: 'Payment Date', value: new Date(paidInvoiceDetails.paidAt).toLocaleDateString() },
                  { label: 'Reference ID', value: paidInvoiceDetails.id.slice(0, 8).toUpperCase(), mono: true },
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                    <span className="text-white/50 text-sm">{row.label}</span>
                    <span className={`text-sm font-medium ${row.highlight ? 'text-emerald-400 font-bold text-base' : row.mono ? 'font-mono text-white/70' : 'text-white'}`}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    const content = `PAYMENT RECEIPT\n${'='.repeat(32)}\n\nBlockBoard Payment Confirmation\n\nDescription: ${paidInvoiceDetails.description}\nAmount Paid: $${paidInvoiceDetails.amount.toFixed(2)}\nPayment Date: ${new Date(paidInvoiceDetails.paidAt).toLocaleDateString()}\nReference ID: ${paidInvoiceDetails.id.slice(0, 8).toUpperCase()}${paidInvoiceDetails.propertyGroupName ? `\nProperty: ${paidInvoiceDetails.propertyGroupName}` : ''}\n\n${'='.repeat(32)}\nThank you for your payment!\n\nThis receipt was generated by BlockBoard.`.trim();
                    const blob = new Blob([content], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `receipt-${paidInvoiceDetails.id.slice(0, 8)}.txt`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                  className="btn-secondary flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Receipt
                </button>
                <button
                  onClick={() => { setShowPaymentSuccess(false); setPaidInvoiceDetails(null); }}
                  className="btn-primary"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
